import React from 'react';
import { Lock, TriangleAlert, UserX, CircleCheck } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { phoneCountryCodes } from '@/lib/phoneCountryCodes';

interface UserBasicInfoFormProps {
  firstName: string;
  setFirstName: (value: string) => void;
  lastName: string;
  setLastName: (value: string) => void;
  email: string;
  setEmail: (value: string) => void;
  originalEmail: string;
  phoneNumber: string;
  setPhoneNumber: (value: string) => void;
  phoneCountryCode: string;
  setPhoneCountryCode: (value: string) => void;
  isActive: boolean;
  setIsActive: (value: boolean) => void;
  loading: boolean;
  isEditMode: boolean;
  isOwnProfile: boolean;
  isAdmin: boolean;
  showPasswordConfirm: boolean;
  setShowPasswordConfirm: (value: boolean) => void;
  showAdminConfirm: boolean;
  setShowAdminConfirm: (value: boolean) => void;
  currentPassword: string;
  setCurrentPassword: (value: string) => void;
}

const UserBasicInfoForm: React.FC<UserBasicInfoFormProps> = ({
  firstName,
  setFirstName,
  lastName,
  setLastName,
  email,
  setEmail,
  originalEmail,
  phoneNumber,
  setPhoneNumber,
  phoneCountryCode,
  setPhoneCountryCode,
  isActive,
  setIsActive,
  loading,
  isEditMode,
  isOwnProfile,
  isAdmin,
  showPasswordConfirm,
  setShowPasswordConfirm,
  showAdminConfirm,
  setShowAdminConfirm,
  currentPassword,
  setCurrentPassword,
}) => {
  const handleEmailChange = (newEmail: string) => {
    setEmail(newEmail);

    // Vis passord-felt hvis bruker endrer sin egen e-post og har passord
    if (isEditMode && isOwnProfile && newEmail !== originalEmail) {
      setShowPasswordConfirm(true);
    } else if (newEmail === originalEmail) {
      setShowPasswordConfirm(false);
      setCurrentPassword('');
    }

    // Vis admin-bekreftelse hvis admin endrer andres e-post
    if (isEditMode && isAdmin && !isOwnProfile && newEmail !== originalEmail) {
      setShowAdminConfirm(true);
    } else if (newEmail === originalEmail) {
      setShowAdminConfirm(false);
    }
  };

  const emailChanged = email !== originalEmail && isEditMode;
  const emailHelperText = !isEditMode
    ? ''
    : email !== originalEmail && isOwnProfile
      ? '⚠️ Du endrer din påloggings-e-post'
      : email !== originalEmail && isAdmin
        ? '⚠️ Du endrer brukerens påloggings-e-post'
        : '';

  return (
    <div className="flex flex-col gap-5">
      {/* Grunnleggende info */}
      <h4 className="text-sm font-semibold -mb-2">Grunnleggende informasjon</h4>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Fornavn *</Label>
          <Input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            disabled={loading}
          />
        </div>
        <div className="space-y-1.5">
          <Label>Etternavn *</Label>
          <Input
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            disabled={loading}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>E-post *</Label>
        <div className="relative">
          <Input
            type="email"
            value={email}
            onChange={(e) => handleEmailChange(e.target.value)}
            required
            disabled={loading}
            className={emailChanged ? 'pr-9' : undefined}
          />
          {emailChanged && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2">
              {isOwnProfile ? (
                <Lock className="h-4 w-4 text-status-waiting" />
              ) : (
                <TriangleAlert className="h-4 w-4 text-destructive" />
              )}
            </span>
          )}
        </div>
        {emailHelperText && <p className="text-xs text-muted-foreground">{emailHelperText}</p>}
      </div>

      {/* Passordbekreftelse ved e-postendring (egen profil) */}
      {showPasswordConfirm && isOwnProfile && (
        <Alert className="border-status-waiting/40 bg-status-waiting/10">
          <AlertDescription className="flex flex-col gap-3">
            <p className="font-semibold">Sikkerhet: Bekreft med passord</p>
            <div className="space-y-1.5">
              <Label>Ditt nåværende passord *</Label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">
                Av sikkerhetsgrunner må du bekrefte med ditt nåværende passord
              </p>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Admin-bekreftelse ved e-postendring (andres profil) */}
      {showAdminConfirm && isAdmin && !isOwnProfile && (
        <Alert>
          <AlertDescription>
            Du endrer e-postadressen til en annen bruker. Dette påvirker deres pålogging.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-4">
        <div className="space-y-1.5">
          <Label>Telefonnummer *</Label>
          <Input
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            required
            disabled={loading}
            placeholder="98765432"
          />
          <p className="text-xs text-muted-foreground">Brukes som kontaktinformasjon for administrator (uten landskode)</p>
        </div>
        <div className="space-y-1.5">
          <Label>Landkode *</Label>
          <Select value={phoneCountryCode} onValueChange={setPhoneCountryCode} disabled={loading}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {phoneCountryCodes.map((country) => (
                <SelectItem key={country.code} value={country.code}>
                  {country.flag} {country.code} {country.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Aktiv/Inaktiv status */}
      <div
        className={cn(
          'p-4 rounded-md border',
          isActive
            ? 'bg-status-confirmed/10 border-status-confirmed/30'
            : 'bg-status-waiting/10 border-status-waiting/30'
        )}
      >
        <div className="flex items-center gap-3">
          <Switch checked={isActive} onCheckedChange={setIsActive} disabled={loading} />
          <div className="flex items-center gap-2">
            {isActive ? (
              <CircleCheck className="h-5 w-5 text-status-confirmed" />
            ) : (
              <UserX className="h-5 w-5 text-status-waiting" />
            )}
            <span className="text-sm font-medium">{isActive ? 'Aktiv bruker' : 'Inaktiv bruker'}</span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1 ml-11">
          {isActive
            ? 'Brukeren kan logge inn i applikasjonen'
            : 'Brukeren er deaktivert og kan ikke logge inn. Bruk "Leser"-rolle for å gi begrenset tilgang.'}
        </p>
      </div>
    </div>
  );
};

export default UserBasicInfoForm;
